import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { walletService } from '../../services/walletService';
import { formatCoins } from '../../utils/walletLabels';
import { StandaloneUnitDto } from '../../types/unit';
import { CreditCard, Wallet, ShoppingCart, X, CheckCircle } from 'lucide-react';
import { cardTheme } from '../../styles/cardTheme';

interface UnitEnrollmentPopupProps {
  isOpen: boolean;
  onClose: () => void;
  unit: StandaloneUnitDto;
  onSuccess?: () => void;
}

const UnitEnrollmentPopup: React.FC<UnitEnrollmentPopupProps> = ({
  isOpen,
  onClose,
  unit,
  onSuccess,
}) => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const displayTitle = unit.titel || unit.unitTitle || unit.unitName;
  const isPaid = unit.price > 0;
  const coinsRequired = Math.round(unit.price);

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setError('');
      setSuccess(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const loadBalance = async () => {
      if (!isOpen || !isAuthenticated) return;
      const result = await walletService.getMyHistory(1, 1);
      if (result.success) {
        setWalletBalance(result.data.history.currentBalance);
      }
    };
    loadBalance();
  }, [isOpen, isAuthenticated]);

  if (!isOpen) return null;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setError('');
      setSuccess(false);
      setIsClosing(false);
    }, 200);
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login', {
        state: {
          redirectTo: '/checkout',
          unit,
          unitId: unit.id,
        },
      });
      return;
    }
    handleClose();
    navigate('/checkout', {
      state: {
        unit,
        unitId: unit.id,
      },
    });
  };

  const handleWalletPurchase = async () => {
    if (!isAuthenticated) {
      setError('يجب تسجيل الدخول أولاً');
      return;
    }

    if (walletBalance !== null && walletBalance < coinsRequired) {
      setError(`رصيدك غير كافٍ. المطلوب: ${formatCoins(coinsRequired)} جنيه`);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      const result = await walletService.buyUnitWithCoins(unit.id);
      if (result.success) {
        setSuccess(true);
        onSuccess?.();
      } else {
        setError(result.error);
      }
    } catch {
      setError('حدث خطأ أثناء شراء الحصة من المحفظة');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToUnit = () => {
    handleClose();
    navigate(`/unit-viewer/${unit.id}`);
  };

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 transition-opacity duration-200 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      dir="rtl"
    >
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      <div
        className={`relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden transform transition-transform duration-200 ${
          isClosing ? 'translate-y-full sm:scale-95' : 'translate-y-0 sm:scale-100'
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className={`text-lg font-bold ${cardTheme.title}`}>الاشتراك في الحصة</h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors min-h-11 min-w-11 flex items-center justify-center"
            aria-label="إغلاق"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {success ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">تم الاشتراك بنجاح!</h3>
              <p className="text-gray-600 mb-6">يمكنك الآن الدخول إلى {displayTitle}</p>
              <button
                type="button"
                onClick={handleGoToUnit}
                className={`w-full py-3 rounded-xl font-semibold ${cardTheme.btnPrimary}`}
              >
                ادخل الحصة الآن
              </button>
            </div>
          ) : (
            <>
              <p className="text-gray-700 mb-1 font-semibold line-clamp-2">{displayTitle}</p>
              <p className="text-sm text-gray-500 mb-6">
                {isPaid
                  ? `السعر: ${unit.price.toLocaleString('ar-EG')} جنيه`
                  : 'هذه الحصة مجانية'}
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                {isPaid ? (
                  <>
                    <button
                      type="button"
                      onClick={handleCheckout}
                      disabled={isLoading}
                      className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold min-h-11 ${cardTheme.btnPrimary} disabled:opacity-50`}
                    >
                      <CreditCard className="w-5 h-5" />
                      الدفع الإلكتروني
                    </button>

                    <button
                      type="button"
                      onClick={handleWalletPurchase}
                      disabled={isLoading}
                      className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold min-h-11 border-2 border-green-700 text-green-700 hover:bg-green-50 disabled:opacity-50`}
                    >
                      <Wallet className="w-5 h-5" />
                      {isLoading
                        ? 'جاري المعالجة...'
                        : `الدفع من المحفظة (${formatCoins(coinsRequired)})`}
                    </button>

                    {walletBalance !== null && walletBalance < coinsRequired && (
                      <p className="text-sm text-amber-700 text-center">
                        رصيدك: {formatCoins(walletBalance)} —{' '}
                        <Link to="/student-portal/wallet" className="underline font-medium" onClick={handleClose}>
                          شحن المحفظة
                        </Link>
                      </p>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handleWalletPurchase}
                    disabled={isLoading}
                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold min-h-11 ${cardTheme.btnPrimary} disabled:opacity-50`}
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {isLoading ? 'جاري المعالجة...' : 'اشترك مجاناً'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnitEnrollmentPopup;
